import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import { useSEO } from "../hooks/useSEO";
import { trackFormSubmission } from "../hooks/useAnalytics";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FadeInSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay }}
    >
      {children}
    </motion.div>
  );
};

const serviceTypes = [
  { value: "diseno_integral", label: "Diseño Naturalista Integral" },
  { value: "proyecto_estrategico", label: "Proyecto Estratégico" },
  { value: "mantenimiento", label: "Cuidados del Jardín" },
  { value: "consulta", label: "Consulta General" }
];

const propertyTypes = [
  { value: "residencial", label: "Residencial" },
  { value: "comercial", label: "Comercial" },
  { value: "desarrollo", label: "Desarrollo Inmobiliario" },
  { value: "hotel", label: "Hotel / Hospitalidad" },
  { value: "otro", label: "Otro" }
];

export default function Contacto() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  useSEO({ title: isEn ? "Contact" : "Contacto", description: "Agenda una visita inicial con CIRCLE Natural Editing. Paisajismo profesional en Los Cabos, BCS." });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    property_type: "",
    location: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error(isEn ? "Please fill in required fields" : "Por favor completa los campos requeridos");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        trackFormSubmission("contact_form");
        toast.success(isEn ? "Message sent! We'll contact you soon." : "¡Mensaje enviado! Te contactaremos pronto.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          service_type: "",
          property_type: "",
          location: "",
          message: ""
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar el mensaje");
      }
    } catch (error) {
      console.error("Contact error:", error);
      toast.error(isEn ? "Connection error" : "Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#F9F6F0]" data-testid="contacto-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs tracking-[0.3em] uppercase text-circulo-sand mb-4 block"
            >
              {isEn ? "Contact" : "Contacto"}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-serif font-medium mb-6"
            >
              {isEn ? "Let's talk about your project" : "Hablemos de tu proyecto"}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted-foreground mb-12"
            >
              {isEn 
                ? "We start with a visit to get to know your property and understand your needs. We'll prepare a personalized proposal."
                : "Comenzamos con una visita para conocer tu propiedad y entender tus necesidades. Preparamos una propuesta personalizada."}
            </motion.p>

            <FadeInSection delay={0.3}>
              <div className="space-y-6">
                <a
                  href="https://maps.google.com/?q=San+Jose+del+Cabo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 border border-white/10 hover:border-circulo-sand/30 transition-colors"
                >
                  <MapPin className="w-5 h-5 text-circulo-sand mt-1" />
                  <div>
                    <span className="text-sm text-muted-foreground block mb-1">
                      {isEn ? "Location" : "Ubicación"}
                    </span>
                    <span>San José del Cabo, Baja California Sur, México</span>
                  </div>
                </a>

                <a
                  href="tel:+524448050926"
                  className="flex items-start gap-4 p-4 border border-white/10 hover:border-circulo-sand/30 transition-colors"
                >
                  <Phone className="w-5 h-5 text-circulo-sand mt-1" />
                  <div>
                    <span className="text-sm text-muted-foreground block mb-1">
                      {isEn ? "Phone" : "Teléfono"}
                    </span>
                    <span>+52 444 805 0926</span>
                  </div>
                </a>

                <a
                  href="mailto:tropicaldesertarq@proton.me"
                  className="flex items-start gap-4 p-4 border border-white/10 hover:border-circulo-sand/30 transition-colors"
                >
                  <Mail className="w-5 h-5 text-circulo-sand mt-1" />
                  <div>
                    <span className="text-sm text-muted-foreground block mb-1">Email</span>
                    <span>tropicaldesertarq@proton.me</span>
                  </div>
                </a>
              </div>
            </FadeInSection>
          </div>

          {/* Right Column - Form */}
          <FadeInSection delay={0.2}>
            <div className="p-8 md:p-12 bg-white border border-[#E6E2D6]">
              <h2 className="text-2xl font-serif mb-8">
                {isEn ? "Request information" : "Solicita información"}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">
                      {isEn ? "Full name" : "Nombre completo"} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                      placeholder={isEn ? "Your name" : "Tu nombre"}
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                      placeholder="tu@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-muted-foreground">
                      {isEn ? "Phone" : "Teléfono"} *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                      placeholder="+52 624 123 4567"
                      data-testid="contact-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="location" className="text-xs uppercase tracking-wider text-muted-foreground">
                      {isEn ? "Property location" : "Ubicación de la propiedad"}
                    </Label>
                    <Input
                      id="location"
                      name="location"
                      type="text"
                      value={formData.location}
                      onChange={handleChange}
                      className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                      placeholder="Ej: Palmilla, San José del Cabo"
                      data-testid="contact-location"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      {isEn ? "Service type" : "Tipo de servicio"}
                    </Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value) => handleSelectChange("service_type", value)}
                    >
                      <SelectTrigger className="mt-2 bg-transparent border-white/20" data-testid="contact-service-type">
                        <SelectValue placeholder={isEn ? "Select a service" : "Selecciona un servicio"} />
                      </SelectTrigger>
                      <SelectContent className="bg-circulo-paper border-white/10">
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      {isEn ? "Property type" : "Tipo de propiedad"}
                    </Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => handleSelectChange("property_type", value)}
                    >
                      <SelectTrigger className="mt-2 bg-transparent border-white/20" data-testid="contact-property-type">
                        <SelectValue placeholder={isEn ? "Select type" : "Selecciona tipo"} />
                      </SelectTrigger>
                      <SelectContent className="bg-circulo-paper border-white/10">
                        {propertyTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs uppercase tracking-wider text-muted-foreground">
                    {isEn ? "Message / Project details" : "Mensaje / Detalles del proyecto"}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand resize-none"
                    placeholder={isEn ? "Tell us about your project..." : "Cuéntanos sobre tu proyecto..."}
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isEn ? "Sending..." : "Enviando..."}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {isEn ? "Send message" : "Enviar mensaje"}
                    </>
                  )}
                </button>
              </form>
            </div>
          </FadeInSection>
        </div>
      </div>
    </div>
  );
}
